(() => {
  'use strict';

  if (window.AONSK_GARDEN_DATA) return;

  const VERSION = '11.84';
  const SCHEMA_VERSION = '1.0.0';
  const STORAGE_KEY = 'aonskGardenWorkspaceV1180';
  const LEGACY_KEYS = Object.freeze([
    'aonskGardenProfileV1162',
    'aonskIntegratedGardenPlanV1166',
    'aonskIntegratedSeedPlanV1166',
    'aonskIntegratedCommunityPlanV1166',
    'aonskIntegratedToolNotesV1166',
    'aonskSavedGardenPlansV1162',
    'aonskSeedPlannerPreferencesV2',
    'aonskSeedPlannerPreferencesV1',
    'anx_zip',
    'anx_dest_full',
    'aonsk_collection_finder_plan_date',
    'aonsk_collection_finder_plan_mode',
    'aonsk_seed_finder_plan_date',
    'aonsk_seed_finder_plan_mode'
  ]);
  const SKILL_DIMENSIONS = Object.freeze([
    'overall', 'seedStarting', 'germination', 'transplanting', 'cropCare',
    'soil', 'diagnosis', 'harvest', 'seedSaving', 'nativePropagation'
  ]);
  const RECORD_STATES = Object.freeze(['known', 'estimated', 'unknown', 'conflicting', 'not_applicable']);
  const CONFIDENCE_LEVELS = Object.freeze(['unknown', 'low', 'moderate', 'high']);

  function now() { return new Date().toISOString(); }
  function id(prefix = 'record') {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return `${prefix}_${window.crypto.randomUUID()}`;
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
  function clone(value) {
    if (typeof structuredClone === 'function') return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
  }
  function parse(value, fallback = null) {
    if (value == null || value === '') return fallback;
    try { return JSON.parse(value); } catch (error) { return value; }
  }
  function storageAvailable() {
    try {
      const probe = '__aonsk_garden_data_probe__';
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      return true;
    } catch (error) { return false; }
  }
  function emit(name, detail) {
    window.dispatchEvent(new CustomEvent(`aonsk:garden-data:${name}`, { detail }));
  }
  function emptySkills() {
    return SKILL_DIMENSIONS.reduce((result, key) => {
      result[key] = 'unassessed';
      return result;
    }, {});
  }
  function createWorkspace(seed = {}) {
    const timestamp = now();
    return {
      schemaVersion: SCHEMA_VERSION,
      workspaceId: seed.workspaceId || id('garden'),
      customerId: seed.customerId || null,
      revision: Number.isInteger(seed.revision) ? seed.revision : 0,
      createdAt: seed.createdAt || timestamp,
      updatedAt: timestamp,
      profile: {
        locations: [],
        spaces: [],
        climateProfiles: [],
        soilReports: [],
        skills: emptySkills(),
        cropExperience: {},
        goals: [],
        preferences: {
          accessibility: {},
          timeCapacity: null,
          budget: null,
          recommendationMode: 'planning'
        },
        consent: {
          accountSync: false,
          preciseLocation: false,
          personalization: false,
          researchSharing: false
        }
      },
      plans: [],
      journal: [],
      learning: { enrollments: [], assessments: [], credentials: [], rewards: [] },
      recommendations: { dismissed: [], saved: [], explanations: [] },
      sync: { state: 'local-only', serverRevision: null, lastSyncedAt: null },
      extensions: {},
      ...clone(seed),
      schemaVersion: SCHEMA_VERSION,
      updatedAt: timestamp
    };
  }
  function validate(workspace) {
    const errors = [];
    if (!workspace || typeof workspace !== 'object') return { valid: false, errors: ['Workspace must be an object.'] };
    if (workspace.schemaVersion !== SCHEMA_VERSION) errors.push(`Unsupported schemaVersion: ${workspace.schemaVersion || 'missing'}.`);
    if (!workspace.workspaceId) errors.push('workspaceId is required.');
    if (!Number.isInteger(workspace.revision) || workspace.revision < 0) errors.push('revision must be a non-negative integer.');
    if (!workspace.profile || typeof workspace.profile !== 'object') errors.push('profile is required.');
    ['plans', 'journal'].forEach(key => { if (!Array.isArray(workspace[key])) errors.push(`${key} must be an array.`); });
    if (!workspace.sync || typeof workspace.sync !== 'object') errors.push('sync is required.');
    return { valid: errors.length === 0, errors };
  }
  function loadLocal() {
    if (!storageAvailable()) return { workspace: null, error: 'storage-unavailable' };
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { workspace: null, error: null };
    const workspace = parse(raw, null);
    const validation = validate(workspace);
    return validation.valid ? { workspace, error: null } : { workspace: null, error: 'invalid-workspace', validation };
  }
  function saveLocal(workspace, options = {}) {
    const validation = validate(workspace);
    if (!validation.valid) return { saved: false, validation };
    if (!storageAvailable()) return { saved: false, error: 'storage-unavailable' };
    const next = clone(workspace);
    next.updatedAt = now();
    if (options.incrementRevision !== false) next.revision += 1;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      emit('saved', { workspaceId: next.workspaceId, revision: next.revision });
      return { saved: true, workspace: next };
    } catch (error) {
      return { saved: false, error: 'storage-write-failed' };
    }
  }
  function legacyKind(key) {
    if (/Profile/i.test(key) || key === 'anx_zip' || key === 'anx_dest_full') return 'profile';
    if (/Plan|Notes/i.test(key)) return 'plan';
    if (/Preferences|finder/i.test(key)) return 'preference';
    return 'unclassified';
  }
  function previewLegacyMigration() {
    const found = [];
    if (!storageAvailable()) return { found, warnings: ['Browser storage is unavailable.'], conflicts: [] };
    const keys = new Set(LEGACY_KEYS);
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && (/^aonsk_tomato_fit_/i.test(key) || /^aonsk(?:LiveCatalog|ManualCropRecords)/.test(key))) keys.add(key);
    }
    keys.forEach(key => {
      const raw = localStorage.getItem(key);
      if (raw == null) return;
      found.push({ key, kind: legacyKind(key), value: parse(raw), bytes: raw.length });
    });
    const dateValues = found.filter(item => /plan_date/i.test(item.key)).map(item => String(item.value || '')).filter(Boolean);
    const zipValues = found.filter(item => item.key === 'anx_zip').map(item => String(item.value || '')).filter(Boolean);
    const conflicts = [];
    if (new Set(dateValues).size > 1) conflicts.push({ field: 'planningDate', values: [...new Set(dateValues)], resolution: 'customer-review-required' });
    if (new Set(zipValues).size > 1) conflicts.push({ field: 'postalCode', values: [...new Set(zipValues)], resolution: 'customer-review-required' });
    return {
      found,
      conflicts,
      warnings: found.length ? ['Legacy values are preserved and have not been changed.'] : [],
      generatedAt: now()
    };
  }
  function attachLegacyPreview(workspace, preview = previewLegacyMigration()) {
    const validation = validate(workspace);
    if (!validation.valid) return { workspace: null, validation };
    const next = clone(workspace);
    next.extensions = next.extensions || {};
    next.extensions.legacyMigration = {
      status: preview.conflicts.length ? 'review-required' : 'ready-for-review',
      generatedAt: preview.generatedAt || now(),
      records: clone(preview.found),
      conflicts: clone(preview.conflicts),
      applied: false
    };
    next.updatedAt = now();
    return { workspace: next, validation: validate(next) };
  }
  function normalizeMonthDay(value) {
    const match = String(value || '').trim().match(/^(?:\d{4}[-/])?(\d{1,2})[-/](\d{1,2})$/);
    if (!match) return '';
    const month = Number(match[1]);
    const day = Number(match[2]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return '';
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
  }
  function normalizeClimateProfile(value = {}, sourceLabel = '') {
    const record = value.data && typeof value.data === 'object' ? value.data : value;
    const monthlyNormals = Array.isArray(record.monthlyNormals) ? record.monthlyNormals.map((month, index) => ({
      month: Number(month.month || index + 1),
      tmaxF: month.tmaxF ?? '',
      tminF: month.tminF ?? '',
      tavgF: month.tavgF ?? '',
      precipIn: month.precipIn ?? '',
      snowIn: month.snowIn ?? '',
      gdd50: month.gdd50 ?? '',
      hotDays90: month.hotDays90 ?? '',
      hotDays100: month.hotDays100 ?? '',
      status: month.status || 'known'
    })).filter(month => month.month >= 1 && month.month <= 12) : [];
    return {
      id: value.id || null,
      zip: String(record.zip || record.postalCode || '').trim(),
      place: record.place || record.city || '',
      state: record.state || record.region || '',
      lat: record.lat ?? record.latitude ?? '',
      lon: record.lon ?? record.longitude ?? '',
      zone: record.zone || record.hardinessZone || '',
      zoneRange: record.zoneRange || '',
      lastFrost: normalizeMonthDay(record.lastFrost),
      firstFrost: normalizeMonthDay(record.firstFrost),
      lastHardFreeze: normalizeMonthDay(record.lastHardFreeze),
      firstHardFreeze: normalizeMonthDay(record.firstHardFreeze),
      frostFreeDays: record.frostFreeDays ?? '',
      hardFreezeFreeDays: record.hardFreezeFreeDays ?? '',
      station: record.station || '',
      stationId: record.stationId || '',
      stationDistance: record.stationDistance ?? '',
      datasetName: record.datasetName || '',
      datasetPeriod: record.datasetPeriod || '',
      retrievedAt: record.retrievedAt || record.updated || value.updatedAt || '',
      monthlyNormals,
      monthlySource: record.monthlySource && typeof record.monthlySource === 'object' ? clone(record.monthlySource) : null,
      annualIndicators: record.annualIndicators && typeof record.annualIndicators === 'object' ? clone(record.annualIndicators) : {},
      droughtTendency: record.droughtTendency || 'unknown',
      humidityTendency: record.humidityTendency || 'unknown',
      windTendency: record.windTendency || 'unknown',
      customerClimateNotes: record.customerClimateNotes || '',
      manualOverrides: record.manualOverrides && typeof record.manualOverrides === 'object' ? clone(record.manualOverrides) : {},
      updated: record.updated || value.updatedAt || '',
      status: value.status || 'known',
      confidence: value.confidence || (record.station ? 'high' : 'moderate'),
      sourceLabel: sourceLabel || value.source?.name || (record.station ? 'saved NOAA climate normal' : 'saved garden profile')
    };
  }
  function readClimateProfile() {
    const loaded = loadLocal();
    const profiles = loaded.workspace?.profile?.climateProfiles;
    if (Array.isArray(profiles) && profiles.length) {
      const activeId = loaded.workspace.profile.activeClimateProfileId;
      if (!activeId && loaded.workspace.profile.climateProfileClearedAt) return null;
      const active = profiles.find(item => item.id === activeId) || profiles.slice().sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))[0];
      return normalizeClimateProfile(active);
    }
    if (!storageAvailable()) return null;
    const legacy = parse(localStorage.getItem('aonskGardenProfileV1162'), null);
    return legacy && typeof legacy === 'object' ? normalizeClimateProfile(legacy, legacy.station ? 'saved NOAA climate normal' : 'saved manual garden profile') : null;
  }
  function writeClimateProfile(profile, options = {}) {
    const normalized = normalizeClimateProfile(profile, options.sourceLabel || 'saved garden profile');
    const loaded = loadLocal();
    const workspace = loaded.workspace || createWorkspace();
    const source = {
      kind: options.sourceKind || (normalized.station ? 'dataset' : 'customer'),
      name: options.sourceName || normalized.sourceLabel,
      retrievedAt: options.retrievedAt || profile.updated || now(),
      version: options.sourceVersion || null,
      url: options.sourceUrl || null
    };
    const record = evidenceRecord('climate-profile', {
      data: normalized,
      status: options.status || 'known',
      confidence: options.confidence || normalized.confidence,
      evidence: Array.isArray(options.evidence) ? options.evidence : [],
      unknowns: Array.isArray(options.unknowns) ? options.unknowns : []
    }, source);
    workspace.profile.climateProfiles.push(record);
    workspace.profile.activeClimateProfileId = record.id;
    workspace.profile.climateProfileClearedAt = null;
    const result = saveLocal(workspace);
    if (result.saved) emit('climate-saved', { profile: normalized, recordId: record.id });
    return { ...result, record };
  }
  function clearClimateProfile() {
    const loaded = loadLocal();
    if (!loaded.workspace) return { saved: true, workspace: null };
    const workspace = loaded.workspace;
    workspace.profile.activeClimateProfileId = null;
    workspace.profile.climateProfileClearedAt = now();
    const result = saveLocal(workspace);
    if (result.saved) emit('climate-cleared', { preservedHistory: true });
    return result;
  }
  function upsertPlan(planType, data, options = {}) {
    const loaded = loadLocal();
    const workspace = loaded.workspace || createWorkspace();
    const externalId = options.externalId || `${planType}:primary`;
    const existingIndex = workspace.plans.findIndex(item => item.externalId === externalId);
    const existing = existingIndex >= 0 ? workspace.plans[existingIndex] : null;
    const record = sourcedRecord('garden-plan', {
      id: existing?.id || id('plan'),
      createdAt: existing?.createdAt || data.created || data.createdAt,
      planType,
      externalId,
      status: options.status || 'known',
      data: clone(data)
    }, {
      kind: options.sourceKind || 'customer',
      name: options.sourceName || 'AONSK planning tool',
      version: options.sourceVersion || VERSION
    });
    if (existingIndex >= 0) workspace.plans.splice(existingIndex, 1, record);
    else workspace.plans.push(record);
    const result = saveLocal(workspace);
    if (result.saved) emit('plan-saved', { planType, externalId, recordId: record.id });
    return { ...result, record };
  }
  function updateProfilePreferences(namespace, values, options = {}) {
    if (!namespace || typeof namespace !== 'string') return { saved: false, error: 'namespace-required' };
    const loaded = loadLocal();
    const workspace = loaded.workspace || createWorkspace();
    workspace.profile.preferences = workspace.profile.preferences || {};
    workspace.profile.preferences[namespace] = {
      values: clone(values),
      updatedAt: now(),
      source: {
        kind: options.sourceKind || 'customer',
        name: options.sourceName || 'AONSK storefront questionnaire',
        version: options.sourceVersion || VERSION
      }
    };
    const result = saveLocal(workspace);
    if (result.saved) emit('preferences-saved', { namespace });
    return result;
  }
  function readProfilePreferences(namespace) {
    const loaded = loadLocal();
    const entry = loaded.workspace?.profile?.preferences?.[namespace];
    return entry?.values && typeof entry.values === 'object' ? clone(entry.values) : null;
  }
  function writeSoilReport(data, options = {}) {
    const loaded = loadLocal();
    const workspace = loaded.workspace || createWorkspace();
    const record = evidenceRecord('soil-report', {
      status: options.status || 'known',
      confidence: options.confidence || (options.laboratory ? 'high' : 'moderate'),
      data: clone(data),
      locationId: options.locationId || null,
      spaceId: options.spaceId || null,
      productContext: options.productContext || null,
      sampledAt: options.sampledAt || null,
      laboratory: options.laboratory || null,
      evidence: Array.isArray(options.evidence) ? options.evidence : [],
      assumptions: Array.isArray(options.assumptions) ? options.assumptions : [],
      unknowns: Array.isArray(options.unknowns) ? options.unknowns : []
    }, {
      kind: options.laboratory ? 'laboratory' : 'customer',
      name: options.sourceName || (options.laboratory ? options.laboratory : 'Customer-entered soil observations'),
      version: options.sourceVersion || VERSION
    });
    workspace.profile.soilReports.push(record);
    workspace.profile.activeSoilReportId = record.id;
    const result = saveLocal(workspace);
    if (result.saved) emit('soil-report-saved', { recordId: record.id, spaceId: record.spaceId });
    return { ...result, record };
  }
  function readLatestSoilReport(options = {}) {
    const loaded = loadLocal();
    let reports = loaded.workspace?.profile?.soilReports;
    if (!Array.isArray(reports) || !reports.length) return null;
    if (options.spaceId) reports = reports.filter(item => item.spaceId === options.spaceId);
    if (options.locationId) reports = reports.filter(item => item.locationId === options.locationId);
    const activeId = loaded.workspace.profile.activeSoilReportId;
    const active = reports.find(item => item.id === activeId);
    return clone(active || reports.slice().sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))[0] || null);
  }
  function sourcedRecord(type, values = {}, source = {}) {
    const timestamp = now();
    return {
      id: values.id || id(type || 'record'),
      type,
      createdAt: values.createdAt || timestamp,
      updatedAt: timestamp,
      status: RECORD_STATES.includes(values.status) ? values.status : 'known',
      source: {
        kind: source.kind || 'customer',
        name: source.name || '',
        retrievedAt: source.retrievedAt || timestamp,
        url: source.url || null,
        version: source.version || null
      },
      ...clone(values)
    };
  }
  function evidenceRecord(type, values = {}, source = {}) {
    return {
      ...sourcedRecord(type, values, source),
      confidence: CONFIDENCE_LEVELS.includes(values.confidence) ? values.confidence : 'unknown',
      evidence: Array.isArray(values.evidence) ? clone(values.evidence) : [],
      assumptions: Array.isArray(values.assumptions) ? clone(values.assumptions) : [],
      unknowns: Array.isArray(values.unknowns) ? clone(values.unknowns) : []
    };
  }

  window.AONSK_GARDEN_DATA = Object.freeze({
    version: VERSION,
    schemaVersion: SCHEMA_VERSION,
    storageKey: STORAGE_KEY,
    legacyKeys: LEGACY_KEYS,
    skillDimensions: SKILL_DIMENSIONS,
    recordStates: RECORD_STATES,
    confidenceLevels: CONFIDENCE_LEVELS,
    createWorkspace,
    validate,
    loadLocal,
    saveLocal,
    previewLegacyMigration,
    attachLegacyPreview,
    readClimateProfile,
    writeClimateProfile,
    clearClimateProfile,
    upsertPlan,
    updateProfilePreferences,
    readProfilePreferences,
    writeSoilReport,
    readLatestSoilReport,
    sourcedRecord,
    evidenceRecord
  });

  emit('ready', { version: VERSION, schemaVersion: SCHEMA_VERSION });
})();
